import React, { useEffect, useState, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, TextField, Box, Typography, useTheme, alpha } from '@mui/material';
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";

// Registrar componentes do Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export const ChartsDate = () => {
    const theme = useTheme();
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                backgroundColor: theme.palette.background.paper,
                titleColor: theme.palette.text.primary,
                bodyColor: theme.palette.text.secondary,
                borderColor: theme.palette.divider,
                borderWidth: 1,
                cornerRadius: 12,
                padding: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: theme.palette.divider,
                    drawBorder: false,
                },
                ticks: { 
                    color: theme.palette.text.secondary,
                    font: { size: 11 }
                }
            },
            x: {
                grid: { display: false },
                ticks: { 
                    color: theme.palette.text.secondary,
                    font: { size: 11 }
                }
            }
        },
    };

    const dataCharts = {
        labels: ticketsData?.data.length > 0 ? ticketsData.data.map((item) => (
            item.hasOwnProperty('horario') ? `${item.horario}:00` : item.data
        )) : [],
        datasets: [
            {
                data: ticketsData?.data.length > 0 ? ticketsData.data.map(item => item.total) : [],
                backgroundColor: theme.palette.primary.main,
                borderRadius: 8,
                barThickness: 16,
                maxBarThickness: 24,
                hoverBackgroundColor: theme.palette.primary.dark
            },
        ],
    };

    const handleGetTicketsInformation = async () => {
        try {
            setIsLoading(true);
            const { data } = await api.get(`/dashboard/ticketsDay?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`);
            setTicketsData(data);
        } catch (error) {
            toast.error('Erro ao buscar informações dos tickets');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.85rem" }}>
                        {i18n.t("dashboard.users.totalAttendances")}
                    </Typography>
                    <Typography variant="h4" fontWeight="800" color="textPrimary">
                        {ticketsData?.count || 0}
                        <Box component="span" sx={{ fontSize: "1rem", color: "textSecondary", ml: 1, fontWeight: "500" }}>
                            atendimentos
                        </Box>
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={initialDate}
                            onChange={(newValue) => setInitialDate(newValue)}
                            label={i18n.t("dashboard.date.initialDate")}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    variant="outlined" 
                                    size="small" 
                                    sx={{ 
                                        width: 160, 
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "12px",
                                            backgroundColor: alpha(theme.palette.background.default, 0.5)
                                        } 
                                    }} 
                                />
                            )}
                        />
                        <DatePicker
                            value={finalDate}
                            onChange={(newValue) => setFinalDate(newValue)}
                            label={i18n.t("dashboard.date.finalDate")}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    variant="outlined" 
                                    size="small" 
                                    sx={{ 
                                        width: 160, 
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "12px",
                                            backgroundColor: alpha(theme.palette.background.default, 0.5)
                                        } 
                                    }} 
                                />
                            )}
                        />
                    </LocalizationProvider>
                    <Button
                        onClick={handleGetTicketsInformation}
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        sx={{ 
                            borderRadius: "50px", 
                            px: 4, 
                            fontWeight: 'bold',
                            textTransform: "none",
                            boxShadow: "none",
                            height: "40px",
                            "&:hover": { boxShadow: theme.shadows[2] }
                        }}
                    >
                        {i18n.t("dashboard.filter.button") || "Filtrar"}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ 
                height: 350, 
                width: '100%', 
                p: 2, 
                borderRadius: "16px",
                bgcolor: alpha(theme.palette.background.default, 0.3),
                border: `1px solid ${theme.palette.divider}`
            }}>
                {ticketsData?.data.length > 0 ? (
                    <Bar options={options} data={dataCharts} />
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary' }}>
                        Nenhum dado encontrado para este período.
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ChartsDate;